<template>
  <div >
    <div
      v-if="loading"
      class="d-flex align-items-center py-50"
    >
      <b-spinner
        small
        variant="primary"
        class="ml-50"
      />
      <span class="text-muted small">جاري تحميل الاقرار...</span>
    </div>

    <b-alert
      v-else-if="loadError"
      show
      variant="danger"
      class="mb-0 py-50"
    >
      {{ loadError }}
    </b-alert>

    <template v-else>
      <!-- Surveyor actions: always visible even if no acknowledgment yet -->
      <div
        v-if="canManageAcknowledgment"
        class="surveyor-ack-row"
      >
        <b-form-checkbox
          v-model="acknowledged"
          :disabled="isSent || saving || sending"
          class="surveyor-ack-check mb-0"
        >
          <span class="surveyor-ack-label">{{ acknowledgmentText }}</span>
        </b-form-checkbox>

        <b-badge
          v-if="isSent"
          variant="success"
          class="flex-shrink-0"
        >
          تم الارسال
        </b-badge>
        <!-- <b-badge
          v-else-if="acknowledgment && acknowledgment.status === 'draft'"
          variant="secondary"
          class="flex-shrink-0"
        >
          مسودة
        </b-badge> -->

        <b-button
          variant="primary"
          size="sm"
          class="flex-shrink-0"
          :disabled="!canSave || saving || sending || isSent"
          @click="handleSave"
        >
          <b-spinner
            v-if="saving"
            small
          />
          <span v-else>{{ actions.save || 'حفظ' }}</span>
        </b-button>

        <b-button
          variant="success"
          size="sm"
          class="flex-shrink-0"
          :disabled="!canSave || saving || sending || isSent"
          @click="openSendModal"
        >
          <b-spinner
            v-if="sending"
            small
          />
          <span v-else>{{ actions.save_and_send || 'حفظ و ارسال' }}</span>
        </b-button>

        <b-button
          v-if="inboxChatId"
          variant="outline-info"
          size="sm"
          class="flex-shrink-0"
          @click="openInMessenger"
        >
          المحادثات
        </b-button>

        <small
          v-if="acknowledgmentMeta"
          class="text-muted surveyor-ack-meta"
        >
          {{ acknowledgmentMeta }}
        </small>
        <small
          v-if="geodesiaStatusLabel"
          class="text-muted surveyor-ack-meta"
        >
          اقرار الجيوديسيا: {{ geodesiaStatusLabel }}
        </small>
      </div>

      <!-- Read-only: always show status (even when not acknowledged yet) -->
      <div
        v-else
        class="surveyor-ack-row"
      >
        <span class="surveyor-ack-label font-weight-bold">
          اقرار المساح
        </span>
        <b-badge
          v-if="isSent"
          variant="success"
          class="flex-shrink-0"
        >
          تم الارسال
        </b-badge>
        <b-badge
          v-else-if="acknowledgment && acknowledgment.status === 'draft'"
          variant="secondary"
          class="flex-shrink-0"
        >
          مسودة
        </b-badge>
        <small
          v-if="acknowledgmentMeta"
          class="text-muted surveyor-ack-meta"
        >
          {{ acknowledgmentMeta }}
        </small>
        <small
          v-else
          class="text-muted surveyor-ack-meta"
        >
          لم يتم الاقرار بعد
        </small>
        <small
          v-if="geodesiaStatusLabel"
          class="text-muted surveyor-ack-meta"
        >
          اقرار الجيوديسيا: {{ geodesiaStatusLabel }}
        </small>
        <b-button
          v-if="inboxChatId"
          variant="outline-info"
          size="sm"
          class="flex-shrink-0"
          @click="openInMessenger"
        >
          المحادثات
        </b-button>
      </div>
    </template>

    <b-modal
      v-model="showSendModal"
      title="ارسال الاقرار"
      centered
      no-close-on-backdrop
      @hidden="onSendModalHidden"
    >
      <b-form-group
        label="المستلمون"
        label-for="surveyor-ack-recipients"
        description="اختر مستخدماً واحداً على الأقل (gisser مكتب فني أو Giser 168)"
      >
        <v-select
          id="surveyor-ack-recipients"
          v-model="selectedRecipients"
          :options="recipientOptions"
          :reduce="option => option.id"
          :get-option-label="option => optionLabel(option)"
          :dir="isRtl ? 'rtl' : 'ltr'"
          :filterable="false"
          :loading="loadingRecipients"
          multiple
          placeholder="ابحث واختر المستلمين..."
          @search="onRecipientSearch"
        >
          <template #option="option">
            <div>
              <span>{{ optionLabel(option) }}</span>
              <small
                v-if="option.email"
                class="text-muted d-block"
              >{{ option.email }}</small>
            </div>
          </template>
          <template #selected-option="option">
            <span>{{ optionLabel(option) }}</span>
          </template>
          <template #no-options>
            لا توجد نتائج
          </template>
        </v-select>
      </b-form-group>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="sending"
          @click="cancel()"
        >
          إلغاء
        </b-button>
        <b-button
          variant="success"
          :disabled="sending || !selectedRecipients.length || !acknowledged"
          @click="handleSaveAndSend"
        >
          <b-spinner
            v-if="sending"
            small
            class="mr-25"
          />
          تأكيد الارسال
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BButton,
  BSpinner,
  BFormCheckbox,
  BFormGroup,
  BBadge,
  BAlert,
  BModal,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  getSurveyorAcknowledgment,
  saveSurveyorAcknowledgment,
  getSurveyorAcknowledgmentRecipients,
  saveAndSendSurveyorAcknowledgment,
} from '@/api/projects/surveyorAcknowledgment'

export default {
  name: 'SurveyorAcknowledgmentSection',
  components: {
    BButton,
    BSpinner,
    BFormCheckbox,
    BFormGroup,
    BBadge,
    BAlert,
    BModal,
    vSelect,
  },
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadError: null,
      saving: false,
      sending: false,
      acknowledgmentText: 'اقرار المساح بأنه القائم علي اعمال الرفع المساحي و المسؤل عنه',
      acknowledgment: null,
      chat: null,
      media: [],
      giserAcknowledgment: null,
      geodesiaAcknowledgment: null,
      actions: {
        save: 'حفظ',
        save_and_send: 'حفظ و ارسال',
      },
      acknowledged: false,
      showSendModal: false,
      recipientOptions: [],
      selectedRecipients: [],
      loadingRecipients: false,
      recipientSearchTimer: null,
    }
  },
  computed: {
    isRtl() {
      return !!(this.$store && this.$store.state && this.$store.state.appConfig
        && (this.$store.state.appConfig.layout?.isRTL || this.$store.state.appConfig.isRTL))
    },
    isSent() {
      return !!(this.acknowledgment && (this.acknowledgment.status === 'sent' || this.acknowledgment.is_sent))
    },
    geodesiaStatusLabel() {
      const geo = this.geodesiaAcknowledgment
      if (!geo) return ''
      if (geo.is_sent || geo.status === 'sent') return 'تم الارسال'
      if (geo.status === 'draft') return 'مسودة'
      return ''
    },
    canSave() {
      return !!this.acknowledged
    },
    canManageAcknowledgment() {
      const hasPerm = typeof this.hasPermission === 'function' && this.hasPermission('مساح بالطبيعة')
      const hasRole = typeof this.hasRole === 'function' && (
        this.hasRole('مساح بالطبيعة') || this.hasRole('admin')
      )
      return !!(hasPerm || hasRole)
    },
    acknowledgmentMeta() {
      if (!this.acknowledgment) return ''
      const parts = []
      if (this.acknowledgment.acknowledged_by_user && this.acknowledgment.acknowledged_by_user.name) {
        parts.push(`أقرّه: ${this.acknowledgment.acknowledged_by_user.name}`)
      }
      if (this.acknowledgment.acknowledged_at) {
        parts.push(this.formatDate(this.acknowledgment.acknowledged_at))
      }
      if (this.isSent && this.acknowledgment.sent_by_user && this.acknowledgment.sent_by_user.name) {
        parts.push(`أرسله: ${this.acknowledgment.sent_by_user.name}`)
      }
      if (this.isSent && this.acknowledgment.sent_at) {
        parts.push(this.formatDate(this.acknowledgment.sent_at))
      }
      return parts.join(' • ')
    },
    inboxChatId() {
      if (this.chat && this.chat.id) return this.chat.id
      if (this.acknowledgment && this.acknowledgment.chat && this.acknowledgment.chat.id) {
        return this.acknowledgment.chat.id
      }
      if (this.acknowledgment && this.acknowledgment.chat_id) return this.acknowledgment.chat_id
      return null
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler(id) {
        if (id) this.loadSection()
      },
    },
  },
  beforeDestroy() {
    if (this.recipientSearchTimer) clearTimeout(this.recipientSearchTimer)
  },
  methods: {
    formatDate(value) {
      if (!value) return ''
      try {
        return new Date(value).toLocaleString('ar-EG')
      } catch (e) {
        return value
      }
    },
    apiErrorMessage(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.message)
        || fallback
        || 'حدث خطأ'
    },
    optionLabel(option) {
      if (!option) return ''
      return option.name || option.full_name || option.email || `#${option.id}`
    },
    applyPayload(data) {
      if (!data) return
      if (data.acknowledgment_text) {
        this.acknowledgmentText = data.acknowledgment_text
      }
      this.acknowledgment = data.acknowledgment || null
      this.chat = data.chat || (data.acknowledgment && data.acknowledgment.chat) || null
      this.media = Array.isArray(data.media) ? data.media : []
      if (data.giser_acknowledgment !== undefined) {
        this.giserAcknowledgment = data.giser_acknowledgment
      }
      if (data.geodesia_acknowledgment !== undefined) {
        this.geodesiaAcknowledgment = data.geodesia_acknowledgment
      }
      if (data.actions) {
        this.actions = { ...this.actions, ...data.actions }
      }

      const ack = this.acknowledgment
      if (ack) {
        this.acknowledged = !!(ack.acknowledged || ack.status === 'sent' || ack.is_sent)
      } else {
        this.acknowledged = false
      }
    },
    async loadSection() {
      this.loading = true
      this.loadError = null
      try {
        const response = await getSurveyorAcknowledgment(this.projectId)
        const data = (response && response.data) || response || {}
        this.applyPayload(data)
      } catch (error) {
        const status = error && error.response && error.response.status
        // No acknowledgment yet is a normal empty state — keep the row visible
        if (status === 404) {
          this.acknowledgment = null
          this.chat = null
          this.media = []
          this.acknowledged = false
          this.loadError = null
        } else {
          this.loadError = this.apiErrorMessage(error, 'تعذر تحميل الاقرار')
        }
      } finally {
        this.loading = false
      }
    },
    async handleSave() {
      if (!this.acknowledged || this.saving || this.isSent) return
      this.saving = true
      try {
        const response = await saveSurveyorAcknowledgment(this.projectId, {
          acknowledged: true,
        })
        const data = (response && response.data) || response || {}
        const payload = data.data || data
        if (payload.acknowledgment || payload.chat || payload.media) {
          this.applyPayload({
            acknowledgment_text: payload.acknowledgment_text || this.acknowledgmentText,
            acknowledgment: payload.acknowledgment || null,
            chat: payload.chat || this.chat,
            media: payload.media || this.media,
            actions: payload.actions || this.actions,
          })
        } else if (payload.id && (payload.status || payload.acknowledged != null)) {
          this.acknowledgment = payload
          this.acknowledged = true
        }
        this.acknowledged = true
        if (this.$toast) {
          this.$toast.success((response && response.message) || data.message || 'تم الحفظ بنجاح')
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error(this.apiErrorMessage(error, 'تعذر حفظ الاقرار'))
        }
      } finally {
        this.saving = false
      }
    },
    openSendModal() {
      if (!this.acknowledged || this.isSent) return
      this.showSendModal = true
      this.selectedRecipients = []
      this.loadRecipients('')
    },
    onSendModalHidden() {
      this.selectedRecipients = []
      this.recipientOptions = []
    },
    onRecipientSearch(search, loading) {
      if (this.recipientSearchTimer) clearTimeout(this.recipientSearchTimer)
      if (loading) loading(true)
      this.recipientSearchTimer = setTimeout(async () => {
        await this.loadRecipients(search)
        if (loading) loading(false)
      }, 300)
    },
    async loadRecipients(keyword = '') {
      this.loadingRecipients = true
      try {
        const response = await getSurveyorAcknowledgmentRecipients(this.projectId, {
          keyword: keyword || undefined,
        })
        const data = (response && response.data) || response || {}
        this.recipientOptions = Array.isArray(data)
          ? data
          : (Array.isArray(data.data) ? data.data : (Array.isArray(data.users) ? data.users : []))
      } catch (error) {
        this.recipientOptions = []
        if (this.$toast) {
          this.$toast.error(this.apiErrorMessage(error, 'تعذر تحميل المستلمين'))
        }
      } finally {
        this.loadingRecipients = false
      }
    },
    async handleSaveAndSend() {
      if (!this.acknowledged) {
        if (this.$toast) this.$toast.error('يجب الموافقة على الاقرار قبل الحفظ')
        return
      }
      if (!this.selectedRecipients.length) {
        if (this.$toast) this.$toast.error('يجب اختيار مستلم واحد على الأقل')
        return
      }

      this.sending = true
      try {
        const response = await saveAndSendSurveyorAcknowledgment(this.projectId, {
          acknowledged: true,
          recipient_user_ids: this.selectedRecipients.map(id => Number(id)),
        })
        const data = (response && response.data) || response || {}
        const payload = data.data || data
        this.applyPayload({
          acknowledgment: payload.acknowledgment || payload,
          chat: payload.chat || null,
          media: payload.media || this.media,
          acknowledgment_text: this.acknowledgmentText,
          actions: this.actions,
        })
        if (payload.chat) this.chat = payload.chat
        if (Array.isArray(payload.media)) this.media = payload.media
        this.acknowledged = true
        this.showSendModal = false
        if (this.$toast) {
          this.$toast.success((response && response.message) || data.message || 'تم الحفظ والارسال بنجاح')
        }
        // Open floating messenger on the chat after send
        const chatId = (payload.chat && payload.chat.id)
          || (this.acknowledgment && this.acknowledgment.chat_id)
          || null
        if (chatId) {
          this.$store.dispatch('surveyChatsUi/open', { chatId })
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error(this.apiErrorMessage(error, 'تعذر حفظ وارسال الاقرار'))
        }
      } finally {
        this.sending = false
      }
    },
    openInMessenger() {
      if (!this.inboxChatId) return
      this.$store.dispatch('surveyChatsUi/open', { chatId: this.inboxChatId })
    },
  },
}
</script>

<style lang="scss" scoped>
.surveyor-ack-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.65rem;
}

.surveyor-ack-check {
  display: inline-flex;
  align-items: center;
  margin: 0;
  max-width: 100%;
}

.surveyor-ack-label {
  font-size: 0.85rem;
  line-height: 1.35;
}

.surveyor-ack-meta {
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.surveyor-acknowledgment-section ::v-deep .vs__dropdown-menu {
  z-index: 1100;
}

.surveyor-acknowledgment-section ::v-deep .custom-control-label {
  font-size: 0.85rem;
}
</style>
